import {useStore} from '@/store/root-store';
import {faChartSimple, faListTree, faMagicWandSparkles, faWrench} from '@fortawesome/pro-duotone-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {toJS} from 'mobx';
import {observer} from 'mobx-react';
import styled from 'styled-components';
import {notification} from '@/utils/notification';
import {LoadingOutlined} from '@ant-design/icons';
import {Spin, Tooltip} from 'antd';
import {useState} from 'react';
import {isScaPublicPage} from '@/utils/url';

interface Props {
  changeView: () => void;
}

export const DrawersTriggers = observer(({changeView}: Props) => {
  const {settings: {customer: {getCustomerQuota}}} = useStore('');
  const {contentOptimizer: {currentPage}} = useStore('');

  const [anyButtonClicked, setAnyButtonClicked] = useState(false);
  const [loadingTaSuggestions, setLoadingTaSuggestions] = useState(false);

  const antIcon = <LoadingOutlined style={{fontSize: '15px', marginRight: 5, color: '#7f4ead'}} spin />;

  const handleTriggerAssistant = async () => {
    // if we are already loading suggetions, return false
    if (currentPage?.taSuggestionsLoading) return false;
    if (!currentPage?.currentEditorState?.getCurrentContent()?.hasText()) return false;

    const addCompleteTerms = (completeTermsLength: number) => {
      const includedTerms = toJS(currentPage?.analytics?.includedTerms);

      prepcCompleteTerms = includedTerms?.sort((a, b) => a.normalizedScore > b.normalizedScore).slice(0, completeTermsLength);
    };

    const sendNoTermsError = () => {
      notification.error('Please add more target keywords to generate new topical terms.', '');
    };
    const includeMoreTerms = toJS(currentPage?.analytics?.includeMoreTerms);

    if (!includeMoreTerms?.length) {
      if (!currentPage?.analytics?.includedTerms?.length) {
        sendNoTermsError();
        return false;
      }
      addCompleteTerms(20);
    }


    let prepIncludeMoreTerms = [];
    let prepcCompleteTerms = [];

    prepIncludeMoreTerms = includeMoreTerms?.sort((a, b) => a.normalizedScore > b.normalizedScore).slice(0, 20);

    if (includeMoreTerms.length < 20) {
      addCompleteTerms(20 - includeMoreTerms.length);
    }

    const finalPrepArr = [...prepIncludeMoreTerms, ...prepcCompleteTerms];
    currentPage?.setFocusTermsForInsertion([...finalPrepArr].map(term => toJS(term)));

    try {
      setAnyButtonClicked(true);
      await currentPage.fetchTaSuggestions(finalPrepArr.map(term => term.name));
      currentPage.setIsTaActive(true);
      getCustomerQuota();
    } catch (e) {
      return Promise.reject(e);
    } finally {
      setAnyButtonClicked(false);
    }
  };

  const buttonsConf = [
    {
      label: 'Headings Outline',
      icon: faListTree,
      hoverColor: '#2D6CCA',
      callback: currentPage?.openCompetitorsHeadingsDrawer,
    },
    {
      label: 'Terms Assistant',
      icon: faMagicWandSparkles,
      hoverColor: '#F58069',
      callback: async () => {
        setLoadingTaSuggestions(true);
        try {
          await handleTriggerAssistant();
        } finally {
          setLoadingTaSuggestions(false);
        }
      },
      loading: currentPage?.taSuggestionsLoading,
      tooltipContent: <div style={{fontSize: 12}}>
        <div style={{fontWeight: 600}}>Launching Terms Assistant.</div>
        <div>Do not modify your article before this tool is fully loaded</div>
      </div>,
    },
    {
      label: 'Technical Audit',
      icon: faWrench,
      hoverColor: '#34AEF3',
      callback: changeView,
      disabled: !currentPage?.content?.importedFromUrl,
    },
    {
      label: 'Competitor Content',
      icon: faChartSimple,
      hoverColor: '#F1AA3E',
      callback: currentPage.openCompetingFocusTermsDrawer,
      loading: currentPage?.isAnalyzing,
    },
  ];


  const updatedButtonConf =() => {
    if (isScaPublicPage()) {
      return buttonsConf.filter(i => i.label !== 'Terms Assistant');
    } else {
      return buttonsConf;
    }
  };

  return <Wrapper>
    {
      updatedButtonConf().map((item, idx) => {
        return <Tooltip
          overlayInnerStyle={{backgroundColor: '#121212', borderRadius: 10}}
          key={idx}
          placement={loadingTaSuggestions ? 'bottom' : 'left'}
          {...((loadingTaSuggestions && idx == 1) && {visible: loadingTaSuggestions})}
          title={item.disabled ? 'Available only for articles imported from URL' : loadingTaSuggestions ? item.tooltipContent : null}>
          <SingleItem anyButtonClicked={anyButtonClicked} hoverColor={item.hoverColor} onClick={() => {
            if (anyButtonClicked || item.disabled) return;
            item.callback();
          }} disabled={item.disabled}>
            {item.loading ? <Spin indicator={antIcon}/> : <FontAwesomeIcon icon={item.icon} style={{marginRight: 7}}/>}
            {item.label}
          </SingleItem>
        </Tooltip>;
      })
    }
  </Wrapper>;
});

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;
const SingleItem = styled.div<{hoverColor: string; anyButtonClicked?: boolean; disabled?: boolean}>`
  width: calc(50% - 5px);
  margin-bottom: 10px;
  transition: background-color .3s ease;
  background-color: #323539;
  cursor: ${p => p.anyButtonClicked || p.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  justify-content: center;
  align-items: center;
  color: #fff;
  padding: 5px;
  border-radius: 6px;
  font-size: 13px;
  opacity: ${p => p.disabled ? .4 : 1};

  &:hover {
    background-color: ${p => !p.anyButtonClicked && p.hoverColor};
  }
`;
